import { useEffect, useState } from 'react'

import { useQuery } from '@connectrpc/connect-query'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { previewBiddingIntradayOrderFormulaPrice } from '@ebalance/proto/bffuser/v1/bidding_intraday_service-BiddingIntradayService_connectquery'
import { skipToken } from '@tanstack/react-query'

import type { IntradayFeeTreatment } from '~/pages/IntradayBidding/hooks/useIntradayFeeTreatment'
import { previewQueryStatus } from '~/pages/IntradayBidding/models/feeAutoConsider'
import {
  type FormulaPricePreviewResult,
  type FormulaPricePreviewTarget,
  resolveIntradayFormulaPricePreview,
  toFormulaPricePreviewRequest,
} from '~/pages/IntradayBidding/models/intradayFormulaPricePreview'

export type IntradayFormulaPricePreviewInput = FormulaPricePreviewTarget

/** 入力を打ち終えるまで問い合わせを待つ時間 (手数料のプレビューと同じ)。 */
const PREVIEW_DEBOUNCE_MS = 300

const keyOf = (input: IntradayFormulaPricePreviewInput | undefined): string => {
  if (input === undefined) return ''
  const { targetDate: d, formula: f } = input
  const ref =
    f.referenceArea.kind === 'systemPrice'
      ? 'system'
      : String(f.referenceArea.areaCode)
  return [
    `${d.year}-${d.month}-${d.day}`,
    input.timeCode,
    input.areaCode,
    input.direction,
    f.variable,
    ref,
    f.operator,
    f.offset,
  ].join(':')
}

/**
 * フォーミュラ価格を、 入力が揃って止まってから backend のプレビュー RPC で引く (AC-BIN-005c)。
 * request の組み立てと応答の読み方は一括作成のコマごとのプレビューと共通
 * (`models/intradayFormulaPricePreview.ts`)。 ここは入力待ち (debounce) と、 入力が揃わない間 (`idle`)
 * を持つ。
 */
export function useIntradayFormulaPricePreview(
  input: IntradayFormulaPricePreviewInput | undefined,
  fee: IntradayFeeTreatment,
): FormulaPricePreviewResult {
  const key = keyOf(input)
  const [settledKey, setSettledKey] = useState(key)
  useEffect(() => {
    const id = setTimeout(() => setSettledKey(key), PREVIEW_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [key])
  const query = input !== undefined && settledKey === key ? input : undefined

  const { data, isFetching, isError, failureCount } = useQuery(
    previewBiddingIntradayOrderFormulaPrice,
    query === undefined
      ? skipToken
      : toFormulaPricePreviewRequest(query, fee.treatment),
    { throwOnError: false },
  )
  const preview = resolveIntradayFormulaPricePreview({
    status:
      input === undefined
        ? 'idle'
        : query === undefined
          ? 'loading'
          : previewQueryStatus({
              waiting: isFetching,
              isError,
              failureCount,
              hasData: data !== undefined,
            }),
    data,
    direction: input?.direction ?? Side.UNSPECIFIED,
    treatment: fee.treatment,
  })
  fee.report(preview.fee)
  return preview
}
